import React, { useState, useEffect } from 'react';
import { Alert } from '../../../utils/Alert';
import AdminSidebar from '../AdminSidebar';
import { MainLayout } from '../../../components/layout';
import Card from '../../../components/ui/Card';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import * as XLSX from 'xlsx';
import '../Admin.css';
import { API_URL } from '../../../config';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';


interface DashboardPageProps {
  user: {
    name: string;
    role: string;
    avatar?: string;
    [key: string]: any;
  };
  onLogout: () => void;
}

interface RecentExam {
  id: number;
  title: string;
  total_attempts: number;
  average_score: number;
}

interface TopPerformer {
  student_id: number;
  name: string;
  total_exams: number;
  average_score: number;
  last_exam_date: string;
}

interface DashboardStats {
  total_students: number;
  total_exams: number;
  average_score: number;
  recent_exams: RecentExam[];
  top_performers: TopPerformer[];
}

interface Exam {
  id: number;
  title: string;
  description: string;
  start_datetime: string;
  end_datetime: string;
  duration_minutes: number;
  max_marks: number;
  created_by: number;
  created_at: string;
  updated_at: string | null;
  creator: {
    username: string;
    email: string;
    id: number;
    role: string;
  };
}

const TIME_PERIODS = [
  { value: 'last_week', label: 'Last Week' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'all', label: 'All Time' },
];

const COLORS = ['#00B578', '#FFB703', '#2563EB', '#8B5CF6', '#F43F5E'];

const AssessmentReports: React.FC<DashboardPageProps> = ({ user, onLogout }) => {
  const [timePeriod, setTimePeriod] = useState('last_month');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exams, setExams] = useState<Exam[]>([]);
  const [selectedExamId, setSelectedExamId] = useState<number | null>(null);
  const [examsLoading, setExamsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const getAuthToken = () => localStorage.getItem('token');

  // Fetch all exams
  const fetchExams = async () => {
    try {
      setExamsLoading(true);
      const token = getAuthToken();
      if (!token) {
        Alert.error('Oops...', 'Authentication required. Please login.');
        return;
      }

      const response = await fetch(`${API_URL}/api/exams/exams/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) throw new Error('Authentication failed. Please login again.');
      if (!response.ok) throw new Error('Failed to fetch exams');
      
      const data = await response.json();
      setExams(data);
    } catch (err: any) {
      setError(err.message);
      if (err.message.includes('Authentication failed')) {
        onLogout();
      }
    } finally {
      setExamsLoading(false);
    }
  };

  // Fetch exams on component mount
  useEffect(() => {
    fetchExams();
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      Alert.error('Oops...', 'Authentication required. Please login.');
      return;
    }
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/api/reports/dashboard?time_period=${timePeriod}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then(res => {
        if (res.status === 401) throw new Error('Authentication failed. Please login again.');
        if (!res.ok) throw new Error('Failed to fetch dashboard stats');
        return res.json();
      })
      .then(data => {
        setStats(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
        if (err.message.includes('Authentication failed')) {
          onLogout();
        }
      });
  }, [timePeriod, onLogout]);

  // Prepare chart data from the API response
  const prepareExamScoreData = () => {
    if (!stats?.recent_exams) return [];
    return stats.recent_exams.map((exam) => ({
      name: exam.title.length > 12 ? exam.title.substring(0, 12) + '...' : exam.title,
      attempts: exam.total_attempts,
      score: exam.average_score,
    }));
  };

  // Prepare pie chart data for attempts distribution
  const prepareAttemptsDistribution = () => {
    if (!stats?.recent_exams) return [];
    return stats.recent_exams.map((exam) => ({
      name: exam.title.length > 12 ? exam.title.substring(0, 12) + '...' : exam.title,
      value: exam.total_attempts,
    }));
  };

  // Score distribution by performer
  const preparePerformerData = () => {
    if (!stats?.top_performers) return [];
    return stats.top_performers.map((performer) => ({
      name: performer.name.length > 12 ? performer.name.substring(0, 12) + '...' : performer.name,
      score: performer.average_score,
      exams: performer.total_exams,
    }));
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const exportToExcel = () => {
    if (!stats) return;

    // Prepare Recent Assessments data
    const recentAssessmentsData = stats.recent_exams.map(exam => ({
      'Assessment Title': exam.title,
      'Total Attempts': exam.total_attempts,
      'Average Score (%)': exam.average_score,
      'Performance Status': exam.average_score >= 80 ? 'Good' : 
                          exam.average_score >= 60 ? 'Average' : 
                          'Needs Improvement'
    }));

    // Prepare Top Performers data
    const topPerformersData = stats.top_performers.map(performer => ({
      'Student Name': performer.name,
      'Total Exams': performer.total_exams,
      'Average Score (%)': performer.average_score,
      'Last Exam Date': new Date(performer.last_exam_date).toLocaleDateString()
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Convert data to worksheets
    const wsAssessments = XLSX.utils.json_to_sheet(recentAssessmentsData);
    const wsPerformers = XLSX.utils.json_to_sheet(topPerformersData);

    // Add the worksheets to the workbook
    XLSX.utils.book_append_sheet(wb, wsAssessments, 'Recent Assessments');
    XLSX.utils.book_append_sheet(wb, wsPerformers, 'Top Performers');

    // Generate Excel file
    XLSX.writeFile(wb, 'assessment_reports.xlsx');
  };

  // Export detailed exam report
  const exportExamReport = async () => {
    if (!selectedExamId) {
      Alert.error('Oops...', 'Please select an exam to export');
      return;
    }

    try {
      setExportLoading(true);
      setError(null);
      
      const token = getAuthToken();
      if (!token) {
        Alert.error('Oops...', 'Authentication required. Please login.');
        return;
      }

      const response = await fetch(
        `${API_URL}/api/reports/exams/${selectedExamId}/detailed?include_question_analysis=true`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 401) throw new Error('Authentication failed. Please login again.');
      
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If response is not valid JSON, treat as empty data
        console.log(`Invalid JSON response for exam ${selectedExamId}, treating as empty data`);
        data = [];
      }
      
      // Handle case where API returns error or empty response
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to fetch exam report');
      }
      
      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      let reportData = data;
      
      // Handle case when no students have taken the exam
      if (!Array.isArray(data) || data.length === 0) {
        console.log(`No student data found for exam ${selectedExamId}, creating placeholder report`);
        const selectedExam = exams.find(exam => exam.id === selectedExamId);
        
        // Create placeholder data structure for empty exam
        reportData = [
          {
            "SL.No": 1,
            "Student Name": "No students have taken this exam",
            "UIN": "N/A",
            "Exam Name": selectedExam?.title || `Exam ${selectedExamId}`,
            "Attempt": 0,
            "Java Correct": 0,
            "Java Incorrect": 0,
            "Java Unanswered": 0,
            "Java Positives": 0,
            "Java Negatives": 0,
            "Java Score": 0,
            "Java Rank": "N/A",
            "Web Correct": 0,
            "Web Incorrect": 0,
            "Web Unanswered": 0,
            "Web Positives": 0,
            "Web Negatives": 0,
            "Web Score": 0,
            "Web Rank": "N/A",
            "Total Correct": 0,
            "Total Incorrect": 0,
            "Total Unanswered": 0,
            "Total Score": 0,
            "Overall Rank": "N/A"
          },
          {
            "SL.No": 2,
            "Student Name": "Exam Status: Not Attempted",
            "UIN": "N/A",
            "Exam Name": selectedExam?.title || `Exam ${selectedExamId}`,
            "Attempt": 0,
            "Java Correct": 0,
            "Java Incorrect": 0,
            "Java Unanswered": 0,
            "Java Positives": 0,
            "Java Negatives": 0,
            "Java Score": 0,
            "Java Rank": "N/A",
            "Web Correct": 0,
            "Web Incorrect": 0,
            "Web Unanswered": 0,
            "Web Positives": 0,
            "Web Negatives": 0,
            "Web Score": 0,
            "Web Rank": "N/A",
            "Total Correct": 0,
            "Total Incorrect": 0,
            "Total Unanswered": 0,
            "Total Score": 0,
            "Overall Rank": "N/A"
          }
        ];
      }
      
      // Convert the detailed report data to worksheet
      const ws = XLSX.utils.json_to_sheet(reportData);

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Exam Detailed Report');

      // Get exam title for filename
      const selectedExam = exams.find(exam => exam.id === selectedExamId);
      const examTitle = selectedExam ? selectedExam.title.replace(/[^a-zA-Z0-9]/g, '_') : 'exam';
      const filename = `exam_${selectedExamId}_${examTitle}_detailed_report.xlsx`;

      // Generate Excel file
      XLSX.writeFile(wb, filename);
      
      // Show success message (optional - you can remove this if not needed)
      console.log(`Exam report exported successfully: ${filename}`);
      
    } catch (err: any) {
      setError(err.message);
      if (err.message.includes('Authentication failed')) {
        onLogout();
      }
    } finally {
      setExportLoading(false);
    }
  };

  const pagination = usePagination(stats?.recent_exams || [], 50);

  return (
    <MainLayout user={user} onLogout={onLogout} sidebarContent={<AdminSidebar />}>
      <div className="min-h-screen bg-gray-50 p-6">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <h1 className="admin-title">Assessment Reports</h1>
            </div>
            {/* <button
              onClick={exportToExcel}
              disabled={!stats || loading}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors bg-green-500 text-white hover:bg-green-600 flex items-center gap-2 ${
                !stats || loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export To Excel
            </button> */}
          </div>
        </div>

        {/* Exam Selection Section */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span className="text-sm font-medium text-gray-600">
                Select Exam {exams.length > 0 && `(${exams.length} available)`}:
              </span>
            </div>
            <div className="relative min-w-[300px]">
              <select
                value={selectedExamId || ''}
                onChange={(e) => setSelectedExamId(e.target.value ? Number(e.target.value) : null)}
                disabled={examsLoading}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-700 focus:border-blue-500 text-sm bg-white"
              >
                <option value="">
                  {examsLoading ? 'Loading exams...' : 'All Exams'}
                </option>
                {exams.map((exam) => (
                  <option key={exam.id} value={exam.id}>
                    {exam.title} {exam.creator?.username ? `- ${exam.creator.username}` : ''}
                  </option>
                ))}
              </select>
              {examsLoading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              )}
            </div>
            {selectedExamId && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Selected:</span>
                <span className="font-medium">
                  {exams.find(exam => exam.id === selectedExamId)?.title}
                </span>
              </div>
            )}
            <div className="ml-auto">
              <button
                onClick={exportExamReport}
                disabled={!selectedExamId || exportLoading}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                  !selectedExamId || exportLoading
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {exportLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Export Report
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Analytics Dashboard Section */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-6">
            {/* New Student Card */}
            <div className="flex-1 min-w-[200px] bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-[#111827]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm text-gray-600 font-kumbh">New Student</div>
                  <div className="text-xl font-bold text-gray-900 font-kumbh">{stats?.total_students || 0}</div>
                  <div className="text-xs text-[#111827] font-kumbh">Enrolled in Assessment</div>
                </div>
              </div>
            </div>

            {/* Total Assessment Card */}
            <div className="flex-1 min-w-[200px] bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm text-gray-600 font-kumbh">Total Assessment</div>
                  <div className="text-xl font-bold text-gray-900 font-kumbh">{stats?.total_exams || 0}</div>
                  <div className="text-xs text-green-600 font-kumbh">Completed this period</div>
                </div>
              </div>
            </div>

            {/* Average Performance Card */}
            <div className="flex-1 min-w-[200px] bg-purple-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm text-gray-600 font-kumbh">Average Performance</div>
                  <div className="text-xl font-bold text-gray-900 font-kumbh">{stats?.average_score.toFixed(2)}%</div>
                  <div className="text-xs text-purple-600 font-kumbh">Overall assessment</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">

          {/* Card 1: Assessment Performance — Grouped Bar, flat X labels, wrap names */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 min-w-0">
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-100">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Assessment Performance</h3>
                <p className="text-xs text-gray-400 mt-0.5">Total attempts alongside average score results</p>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-md bg-slate-100 text-slate-500 font-medium border border-slate-200">
                {prepareExamScoreData().length} Assessments
              </span>
            </div>
            <ResponsiveContainer width="99%" height={320}>
              <BarChart
                data={prepareExamScoreData()}
                margin={{ top: 10, right: 16, left: 0, bottom: 20 }}
                barCategoryGap="30%"
                barGap={4}
              >
                <CartesianGrid strokeDasharray="0" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  interval={0}
                  angle={-40}
                  textAnchor="end"
                  height={70}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  allowDecimals={false}
                  tickCount={5}
                />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 12 }}
                  cursor={{ fill: 'rgba(99,102,241,0.05)' }}
                />
                <Legend
                  verticalAlign="bottom"
                  wrapperStyle={{ paddingTop: 16, fontSize: 12 }}
                  iconType="circle"
                  iconSize={8}
                />
                <Bar dataKey="score" name="Average Score (%)" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={14} minPointSize={3} />
                <Bar dataKey="attempts" name="Total Attempts" fill="#10b981" radius={[4, 4, 0, 0]} barSize={14} minPointSize={3} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Card 2: Attempts Distribution — Donut + bottom legend with % */}
          {(() => {
            const distData = prepareAttemptsDistribution();
            const totalAttempts = distData.reduce((s, d) => s + d.value, 0);
            const DIST_COLORS = ['#f59e0b', '#f97316', '#06b6d4', '#8b5cf6', '#10b981', '#6366f1', '#ec4899'];
            return (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 min-w-0">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-800">Attempts Distribution</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Proportional engagement share per assessment</p>
                </div>
                <ResponsiveContainer width="99%" height={320}>
                  <PieChart>
                    <Pie
                      data={distData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="45%"
                      innerRadius={80} outerRadius={130}
                      paddingAngle={2}
                    >
                      {distData.map((_, i) => (
                        <Cell key={i} fill={DIST_COLORS[i % DIST_COLORS.length]} stroke="#fff" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 12 }}
                      formatter={(v: number, name: any) => {
                        const pct = totalAttempts > 0 ? ((v / totalAttempts) * 100).toFixed(0) : 0;
                        return [`${v} attempts (${pct}%)`, name];
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ paddingTop: 12, fontSize: 11 }}
                      formatter={(value, entry: any) => {
                        const total = distData.reduce((s, d) => s + d.value, 0);
                        const pct = total > 0 ? ((entry.payload.value / total) * 100).toFixed(0) : 0;
                        return <span style={{ color: '#334155', fontSize: 11 }}>{value} ({pct}%)</span>;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            );
          })()}

        </div>



        {/* Recent Assessments Table */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 font-kumbh">Recent Assessments Data</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Assessment</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Total Attempts</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Average Score</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Performance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pagination.paginatedData.map((exam) => (
                  <tr key={exam.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{exam.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-center">{exam.total_attempts}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-center">{exam.average_score}%</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              exam.average_score >= 80 ? 'bg-green-500' : 
                              exam.average_score >= 60 ? 'bg-yellow-500' : 
                              'bg-red-500'
                            }`} 
                            style={{ width: `${exam.average_score}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium ${
                          exam.average_score >= 80 ? 'text-green-600' : 
                          exam.average_score >= 60 ? 'text-yellow-600' : 
                          'text-red-600'
                        }`}>
                          {exam.average_score >= 80 ? 'Good' : 
                           exam.average_score >= 60 ? 'Average' : 
                           'Need Improvement'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination {...pagination} onPageChange={pagination.setCurrentPage} onItemsPerPageChange={pagination.setItemsPerPage} />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AssessmentReports; 
